// studioReturn — the way back to the Studio from a detail opened on its
// canvas (a card or a part). Pure: the frame's focus history says where the
// detail was entered from, and a key press whether it asks to leave.
//
// Interim for a focus sheet over the canvas (B-07): the detail is the
// Library's (or the Knowledge bank's) own, with an explicit "Back to Studio"
// and Esc; browser Back already returns (the Studio keeps its zoom, pan and
// selection on its own).

export { cameFromStudio, isLeaveKey, leaveKeyOf };

import type { KeyboardEvent } from "react";
import type { FocusHistory } from "./focusHistory.js";

// The view focused just before this one was the Studio.
const cameFromStudio = (history: FocusHistory): boolean =>
    history.back.at(-1)?.type === "studio";

type LeaveKey = {
    readonly key: string;
    readonly modified: boolean;
    // Typed into a field, an editor or an open menu (they own Esc).
    readonly inField: boolean;
    // Something nearer already handled it (a dialog closing).
    readonly handled: boolean;
};

// What owns its own Esc: a field, an editor, an open menu or dialog.
const ESC_OWNERS =
    "input, textarea, select, [contenteditable]:not([contenteditable='false']), [role='menu'], [role='dialog']";

const leaveKeyOf = (e: KeyboardEvent): LeaveKey => ({
    key: e.key,
    modified: e.metaKey || e.ctrlKey || e.altKey || e.shiftKey,
    inField:
        e.target instanceof Element && e.target.closest(ESC_OWNERS) !== null,
    handled: e.defaultPrevented,
});

// A plain Esc no one nearer has claimed.
const isLeaveKey = (k: LeaveKey): boolean =>
    k.key === "Escape" && !k.modified && !k.inField && !k.handled;
